import React, { useState } from 'react';
import { Check, Copy, Loader2, X } from 'lucide-react';
import { supportAPI } from '../../services/supportApi';

// Κοινά βοηθήματα του Support panel (labels, badges, copy, φόρμα ticket).

export const TICKET_TYPES: { value: string; label: string }[] = [
  { value: 'complaint', label: 'Παράπονο' },
  { value: 'request', label: 'Αίτημα' },
  { value: 'incident', label: 'Βλάβη' },
  { value: 'question', label: 'Ερώτηση' },
];
export const TICKET_PRIORITIES: { value: string; label: string }[] = [
  { value: 'low', label: 'Χαμηλή' },
  { value: 'normal', label: 'Κανονική' },
  { value: 'high', label: 'Υψηλή' },
  { value: 'urgent', label: 'Επείγον' },
];
export const TICKET_STATUSES: { value: string; label: string }[] = [
  { value: 'open', label: 'Ανοιχτό' },
  { value: 'in_progress', label: 'Σε εξέλιξη' },
  { value: 'resolved', label: 'Επιλυμένο' },
];

export const typeLabel = (v: string) => TICKET_TYPES.find(t => t.value === v)?.label || v;
export const priorityLabel = (v: string) => TICKET_PRIORITIES.find(t => t.value === v)?.label || v;
export const statusLabel = (v: string) => TICKET_STATUSES.find(t => t.value === v)?.label || v;

export const dstr = (d?: string | null) => (d ? String(d).slice(0, 10) : '');
export const dfmt = (d?: string | null) => {
  if (!d) return '—';
  const [y, m, day] = dstr(d).split('-');
  return `${day}/${m}/${y}`;
};
export const dtfmt = (d?: string | null) => {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleString('el-GR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
};

export const TypeBadge: React.FC<{ type: string }> = ({ type }) => {
  const cls = type === 'complaint' ? 'bg-red-500/15 text-red-700 dark:text-red-300'
    : type === 'incident' ? 'bg-orange-500/15 text-orange-700 dark:text-orange-300'
    : type === 'question' ? 'bg-sky-500/15 text-sky-700 dark:text-sky-300'
    : 'bg-surface-3 dark:bg-surface-3/40 text-t2';
  return <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${cls}`}>{typeLabel(type)}</span>;
};

export const PriorityBadge: React.FC<{ priority: string }> = ({ priority }) => {
  if (priority === 'normal') return null;
  const cls = priority === 'urgent' ? 'bg-red-500/20 text-red-700 dark:text-red-300 font-semibold'
    : priority === 'high' ? 'bg-amber-500/20 text-amber-800 dark:text-amber-300'
    : 'bg-surface-3 dark:bg-surface-3/40 text-t3';
  return <span className={`text-[11px] px-2 py-0.5 rounded-full ${cls}`}>{priorityLabel(priority)}</span>;
};

export const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cls = status === 'open' ? 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-300'
    : status === 'in_progress' ? 'bg-amber-500/15 text-amber-800 dark:text-amber-300'
    : 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300';
  return <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${cls}`}>{statusLabel(status)}</span>;
};

// Κουμπί αντιγραφής (AnyDesk ID/password κ.λπ.)
export const CopyButton: React.FC<{ value: string; title?: string }> = ({ value, title }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // Fallback για μη-secure contexts
      const ta = document.createElement('textarea');
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={copy} title={title || 'Αντιγραφή'} className="p-1 text-t3 hover:text-cyan-700 dark:hover:text-cyan-300 rounded">
      {copied ? <Check className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
};

// Φόρμα νέου ticket — χρησιμοποιείται από το tab «Αιτήματα» και από την κάρτα
// καταστήματος (fixedStoreId).
export const TicketFormModal: React.FC<{
  stores: { id: number; name: string; display_name?: string | null }[];
  fixedStoreId?: number;
  onClose: () => void;
  onCreated: () => void;
}> = ({ stores, fixedStoreId, onClose, onCreated }) => {
  const [form, setForm] = useState({
    store_id: fixedStoreId ? String(fixedStoreId) : '',
    type: 'request', priority: 'normal', title: '', description: '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const storeId = parseInt(form.store_id);
    if (!Number.isInteger(storeId)) { alert('Διάλεξε κατάστημα.'); return; }
    if (!form.title.trim()) { alert('Δώσε τίτλο.'); return; }
    try {
      setSaving(true);
      await supportAPI.createTicket({
        store_id: storeId, type: form.type, priority: form.priority,
        title: form.title.trim(), description: form.description.trim() || undefined,
      });
      onCreated();
      onClose();
    } catch (e: any) {
      alert(e.response?.data?.error || 'Αποτυχία δημιουργίας ticket');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full bg-surface-2 border border-line-strong text-t1 rounded-lg px-3 py-2 text-sm placeholder:text-t4';
  const labelCls = 'text-xs text-t3 block mb-1';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-line">
          <h3 className="text-lg font-bold text-t1">Νέο ticket</h3>
          <button onClick={onClose} className="p-1 text-t3 hover:text-t1"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className={labelCls}>Κατάστημα</label>
            <select
              value={form.store_id} disabled={!!fixedStoreId}
              onChange={(e) => setForm({ ...form, store_id: e.target.value })}
              className={`${inputCls} disabled:opacity-60`}
            >
              <option value="">— Επιλογή —</option>
              {stores.map(s => <option key={s.id} value={s.id}>{s.display_name || s.name} (#{s.id})</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Τύπος</label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputCls}>
                {TICKET_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Προτεραιότητα</label>
              <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className={inputCls}>
                {TICKET_PRIORITIES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Τίτλος</label>
            <input
              value={form.title} maxLength={200}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="π.χ. Δεν εκτυπώνει ο θερμικός στο ταμείο"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Περιγραφή</label>
            <textarea
              value={form.description} rows={4}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Λεπτομέρειες, τι ειπώθηκε στο τηλέφωνο, βήματα αναπαραγωγής…"
              className={`${inputCls} resize-y`}
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-line">
          <button onClick={onClose} className="px-4 py-2 text-sm text-t3 hover:text-t1">Άκυρο</button>
          <button
            onClick={submit} disabled={saving}
            className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white rounded-lg text-sm font-medium flex items-center gap-2"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Δημιουργία
          </button>
        </div>
      </div>
    </div>
  );
};
